import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, useCart } from '@/contexts';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import CartDrawer from './CartDrawer';
import NotificationBell from '@/components/notifications/NotificationBell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { ShoppingCart, User, LogOut, Menu, X, Settings, Search, Palette, Leaf, MessageCircle } from 'lucide-react';

const Header = () => {
  const { user, logout, isAdmin } = useAuth();
  const { cart } = useCart();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { t, i18n } = useTranslation();

  const cartItemsCount = cart.items.reduce((sum, item) => sum + item.quantity, 0);

  // 处理搜索 - 使用window.location进行导航（Safari兼容）
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/search?q=${encodeURIComponent(searchQuery.trim())}`;
      setSearchQuery('');
    }
  };

  // 导航栏状态 - 滚动时切换背景
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // 初始检查
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 文字颜色 - 统一琥珀色系
  const textColor = 'text-amber-900';
  const textColorSecondary = 'text-amber-700 hover:text-amber-900';

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 h-14 transition-colors duration-300 ${
        isScrolled ? 'bg-amber-50 shadow-sm' : 'bg-amber-50'
      }`} 
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-6 h-full">
        <div className="flex items-center justify-between h-full">
          {/* Logo - 纯文字 */}
          <Link to="/" className="flex items-center flex-shrink-0" data-testid="logo-link">
            <span className={`text-xl font-light tracking-tight ${textColor}`}>
              ArtCanvas
            </span>
          </Link>

          {/* Desktop Nav - 小米风格极简导航 */}
          <nav className="hidden lg:flex items-center space-x-4">
            <Link 
              to="/artcanvas" 
              className={`text-sm font-medium transition-colors text-center leading-tight max-w-[80px] ${textColorSecondary}`}
              data-testid="nav-artcanvas"
            >
              {t('nav.artCanvas')}
            </Link>
            <Link 
              to="/community" 
              className={`text-sm font-medium transition-colors text-center leading-tight max-w-[80px] ${textColorSecondary}`}
              data-testid="nav-community"
            >
              {t('nav.community')}
            </Link>
            
            {/* 搜索框 - 极简风格 */}
            <form onSubmit={handleSearch} className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-500" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('common.search') + '...'}
                className="w-40 h-8 pl-9 pr-3 text-sm rounded-full border-0 bg-amber-100/50 text-amber-900 placeholder:text-amber-500 focus:bg-amber-100"
                data-testid="header-search-input"
              />
            </form>
          </nav>

          {/* Actions - 极简右侧操作区 */}
          <div className="flex items-center space-x-3">
            {/* Mobile Menu Toggle */}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)} 
              className={`lg:hidden ${textColorSecondary}`}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>

            {/* Cart */}
            <Sheet open={cartOpen} onOpenChange={setCartOpen}>
              <SheetTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className={`relative ${textColorSecondary}`} 
                  data-testid="cart-button"
                >
                  <ShoppingCart className="w-5 h-5" />
                  {cartItemsCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-600 text-white text-xs rounded-full flex items-center justify-center">
                      {cartItemsCount}
                    </span>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent className="w-full sm:max-w-lg">
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5" />
                    {t('cart.title')} ({cartItemsCount})
                  </SheetTitle>
                </SheetHeader>
                <CartDrawer onClose={() => setCartOpen(false)} />
              </SheetContent>
            </Sheet>

            {/* User Actions */}
            {user ? (
              <div className="hidden lg:flex items-center space-x-1">
                {/* 通知铃铛 */}
                <NotificationBell />
                
                {/* 私信 */}
                <Link 
                  to="/messages"
                  data-testid="messages-button"
                  className={`inline-flex items-center justify-center h-9 w-9 rounded-md ${textColorSecondary}`}
                >
                  <MessageCircle className="w-5 h-5" />
                </Link>
                
                {/* 个人主页 */}
                <Link 
                  to={`/profile/${user.id}`}
                  data-testid="my-profile-button"
                  className={`inline-flex items-center justify-center h-9 px-3 rounded-md text-sm ${textColorSecondary}`}
                >
                  <User className="w-4 h-4 mr-1" />
                  {t('nav.profile')}
                </Link>
                
                {/* 创作者中心 */}
                <Link 
                  to="/creator-center"
                  data-testid="creator-center-button"
                  className={`inline-flex items-center justify-center h-9 px-3 rounded-md text-sm ${textColorSecondary}`}
                >
                  <Palette className="w-4 h-4 mr-1" />
                  {t('nav.creatorCenter')}
                </Link>
                {isAdmin && (
                  <Link 
                    to="/admin"
                    data-testid="admin-button"
                    className={`inline-flex items-center justify-center h-9 px-3 rounded-md text-sm ${textColorSecondary}`}
                  >
                    <Settings className="w-4 h-4" />
                  </Link>
                )}
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={logout} 
                  data-testid="logout-button"
                  className={textColorSecondary}
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <Link 
                to="/login"
                className={`hidden lg:flex items-center justify-center h-9 px-3 rounded-md text-sm ${textColorSecondary}`}
                data-testid="login-button"
              >
                <User className="w-4 h-4 mr-1" />
                {t('nav.login')}
              </Link>
            )}

            {/* 语言切换 - 隐藏在桌面端 */}
            <div className="hidden lg:block">
              <LanguageSwitcher />
            </div>
          </div>
        </div>

        {/* Mobile Nav - 极简全屏菜单 */}
        {mobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 top-16 bg-amber-50 z-40">
            <nav className="flex flex-col p-6 space-y-6">
              <Link 
                to="/artcanvas"
                onClick={() => setMobileMenuOpen(false)}
                className="text-2xl font-light text-amber-900 text-left"
              >
                {t('nav.artCanvas')}
              </Link>
              <Link 
                to="/community"
                onClick={() => setMobileMenuOpen(false)}
                className="text-2xl font-light text-amber-900 text-left"
              >
                {t('nav.community')}
              </Link>
              
              <div className="border-t border-amber-200 pt-6">
                {user ? (
                  <>
                    <Link 
                      to={`/profile/${user.id}`}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center text-lg text-amber-700 mb-4"
                      data-testid="mobile-my-profile-button"
                    >
                      <User className="w-5 h-5 mr-3" />
                      {t('nav.profile')}
                    </Link>
                    <Link 
                      to="/creator-center"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center text-lg text-amber-700 mb-4"
                    >
                      <Palette className="w-5 h-5 mr-3" />
                      {t('nav.creatorCenter')}
                    </Link>
                    <Link 
                      to="/orders"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center text-lg text-amber-700 mb-4"
                    >
                      {t('nav.myOrders')}
                    </Link>
                    {isAdmin && (
                      <Link 
                        to="/admin"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center text-lg text-amber-700 mb-4"
                      >
                        <Settings className="w-5 h-5 mr-3" />
                        {t('nav.admin')}
                      </Link>
                    )}
                    <button 
                      onClick={() => { logout(); setMobileMenuOpen(false); }} 
                      className="flex items-center text-lg text-red-600"
                    >
                      <LogOut className="w-5 h-5 mr-3" />
                      {t('nav.logout')}
                    </button>
                  </>
                ) : (
                  <Link 
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center text-lg text-amber-900"
                  >
                    <User className="w-5 h-5 mr-3" />
                    {t('nav.login')} / {t('nav.register')}
                  </Link>
                )}
              </div>
              
              <div className="pt-4">
                <LanguageSwitcher />
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
